import { DndContext, type DragEndEvent, PointerSensor, useDndContext, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { isUndefined } from 'lodash';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { setCurrentConfigurableViewVisibleFieldsAction, setSortingFieldAction } from '~/store/configViews/actions';
import { currentSortFieldNameSelector } from '~/store/configViews/selectors';
import { type ConfigViewOption } from '~/store/configViews/types';
import {
  assignSortOrder,
  collisionDetection,
  excludeHeaderOptions,
  getCurrentConfigurableViewHiddenFieldsOrder,
  hiddenHeader,
  setCurrentConfigurableViewHiddenFieldsOrder,
  snapItemToCursor,
  sortBySortOrder,
  visibleHeader,
} from '~/utils/configurableViewFieldsUtils';
import { useAirStore } from '~/utils/ReduxUtils';
import ViewVisibilityHeader from './ViewVisibilitySection/ViewVisibilityHeader';
import { ViewVisibilityToggleSortable } from './ViewVisibilitySection/ViewVisibilityToggleSortable';

type ConfigViewVisibilityOptionsSortableProps = {
  metadataOptions: ConfigViewOption[];
  customFieldOptions: ConfigViewOption[];
};

export const ConfigViewVisibilityOptionsSortable = memo(
  ({ metadataOptions, customFieldOptions }: ConfigViewVisibilityOptionsSortableProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const store = useAirStore();
    const dispatch = useDispatch();
    const boardId = useSelector(centralizedBoardIdSelector) ?? 'generic';

    const configViewOptions = useMemo(() => {
      const hiddenFieldIds = getCurrentConfigurableViewHiddenFieldsOrder(boardId) ?? [];

      const availableFields = [...metadataOptions, ...customFieldOptions];

      const visibleFields = availableFields.filter((field) => field.isVisible).sort(sortBySortOrder);

      const hiddenFields = availableFields
        .filter((field) => !field.isVisible)
        .map((field) => ({
          ...field,
          ...(isUndefined(field.sortOrder) && {
            sortOrder: hiddenFieldIds.indexOf(field.derivedId) + 1 + visibleFields.length,
          }),
        }))
        .sort(sortBySortOrder);

      return [visibleHeader, ...visibleFields, hiddenHeader, ...hiddenFields];
    }, [metadataOptions, customFieldOptions, boardId]);

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
          return;
        }

        const oldIndex = configViewOptions.findIndex((option) => option.derivedId === active.id);
        const newIndex = configViewOptions.findIndex((option) => option.derivedId === over.id);
        // Prevent dropping above the visible-header (index 0)
        if (newIndex === 0) {
          return;
        }
        const newOptions = arrayMove(configViewOptions, oldIndex, newIndex);

        // Determine if we're moving between sections by checking if the visibility changed
        const activeOption = configViewOptions.find((option) => option.derivedId === active.id);
        const overOption = configViewOptions.find((option) => option.derivedId === over.id);

        const orderMap = newOptions
          .filter(excludeHeaderOptions)
          .reduce<Record<string, number>>((acc, option, index) => {
            acc[option.derivedId] = index + 1;
            return acc;
          }, {});
        orderMap['name'] = 0;

        const updatedConfigViewOptions = configViewOptions
          .filter(excludeHeaderOptions)
          .map((option) => {
            const isChangedVisibility = Boolean(
              activeOption &&
                option.derivedId === activeOption.derivedId &&
                ((overOption && activeOption.isVisible !== overOption.isVisible) || over.id === hiddenHeader.derivedId),
            );

            return {
              ...option,
              isVisible: isChangedVisibility ? !option.isVisible : option.isVisible,
              sortOrder: orderMap[option.derivedId],
            };
          })
          .sort(sortBySortOrder);

        setCurrentConfigurableViewHiddenFieldsOrder(
          boardId,
          updatedConfigViewOptions.filter((option) => !option.isVisible).map((option) => option.derivedId),
        );

        dispatch(
          setCurrentConfigurableViewVisibleFieldsAction({
            fields: updatedConfigViewOptions
              .filter((option) => option.isVisible)
              .map((option) => ({
                name: option.fieldName,
                customFieldId: option.customFieldId,
                sortOrder: option.sortOrder,
              })),
          }),
        );
      },
      [configViewOptions, boardId, dispatch],
    );

    const handleToggleVisibilitySingular = useCallback(
      (item: ConfigViewOption) => {
        const updatedIsVisible = !item.isVisible;

        const visibleFields = configViewOptions
          .filter(excludeHeaderOptions)
          .filter((option) => option.isVisible && option.derivedId !== item.derivedId);
        const hiddenFields = configViewOptions
          .filter(excludeHeaderOptions)
          .filter((option) => !option.isVisible && option.derivedId !== item.derivedId);

        if (updatedIsVisible === true) {
          // add the item to the bottom of the visible fields
          visibleFields.push({ ...item, isVisible: true });
        } else if (updatedIsVisible === false) {
          // add the item to the top of the hidden fields
          hiddenFields.unshift({ ...item, isVisible: false });
        }

        assignSortOrder(visibleFields);
        assignSortOrder(hiddenFields);

        setCurrentConfigurableViewHiddenFieldsOrder(
          boardId,
          hiddenFields.map((option) => option.derivedId),
        );
        dispatch(
          setCurrentConfigurableViewVisibleFieldsAction({
            fields: visibleFields.map((option) => ({
              name: option.fieldName,
              customFieldId: option.customFieldId,
              sortOrder: option.sortOrder,
            })),
          }),
        );

        const sort = currentSortFieldNameSelector(store.getState());
        if (updatedIsVisible === false && item.fieldName === sort) {
          // default to name column sort if this toggle is going to disappear
          dispatch(setSortingFieldAction({ sortingFieldName: 'name' }));
        }
      },
      [configViewOptions, boardId, dispatch, store],
    );

    const handleToggleVisibilityBulk = useCallback(
      (updatedIsVisible: boolean) => {
        const visibleFields = configViewOptions.filter(excludeHeaderOptions).filter((option) => option.isVisible);
        const hiddenFields = configViewOptions.filter(excludeHeaderOptions).filter((option) => !option.isVisible);

        if (updatedIsVisible === true) {
          visibleFields.push(...hiddenFields);
          assignSortOrder(visibleFields);

          // hidden fields explicitly set to empty array, since we're showing them all
          setCurrentConfigurableViewHiddenFieldsOrder(boardId, []);
          dispatch(
            setCurrentConfigurableViewVisibleFieldsAction({
              fields: visibleFields.map((option) => ({
                name: option.fieldName,
                customFieldId: option.customFieldId,
                sortOrder: option.sortOrder,
              })),
            }),
          );
        } else if (updatedIsVisible === false) {
          hiddenFields.unshift(...visibleFields);
          assignSortOrder(hiddenFields);

          setCurrentConfigurableViewHiddenFieldsOrder(
            boardId,
            hiddenFields.map((option) => option.derivedId),
          );
          // visible fields explicitly set to empty array, since we're hiding them all
          dispatch(
            setCurrentConfigurableViewVisibleFieldsAction({
              fields: [],
            }),
          );
        }
      },
      [configViewOptions, boardId, dispatch],
    );

    const { active } = useDndContext();
    const isDragging = Boolean(active);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      }),
    );

    return (
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, snapItemToCursor(containerRef)]}
        collisionDetection={collisionDetection}
      >
        <div className="flex flex-col" ref={containerRef}>
          <SortableContext
            items={configViewOptions.map((item) => item.derivedId)}
            strategy={verticalListSortingStrategy}
          >
            {configViewOptions.map((item) =>
              item.derivedId === visibleHeader.derivedId || item.derivedId === hiddenHeader.derivedId ? (
                <ViewVisibilityHeader key={item.derivedId} option={item} onClick={handleToggleVisibilityBulk} />
              ) : (
                <ViewVisibilityToggleSortable
                  key={item.derivedId}
                  onClick={() => handleToggleVisibilitySingular(item)}
                  option={item}
                  isDraggable
                  isDragging={isDragging}
                />
              ),
            )}
          </SortableContext>
        </div>
      </DndContext>
    );
  },
);

ConfigViewVisibilityOptionsSortable.displayName = 'ConfigViewVisibilityOptionsSortable';
